import { useState } from 'react';
import { useNavigate } from 'react-router';
import { buildMeta } from '~/lib/seo';
import { SITE } from '~/data/site';
import { FACILITY_TYPES } from '@shared/types';
import type { LeadSubmissionPayload, LeadSubmissionResponse } from '@shared/types';
import { FACILITY_LABELS, FREQUENCY_TIERS } from '@shared/pricing';
import { isValidPhone } from '@shared/utils';

export function meta() {
  return buildMeta({
    title: 'Get a quote',
    description:
      'Seven fields and a walkthrough. Tell us about the building and we give you a fixed price in writing — no annual contract, no estimate that shifts later.',
    path: '/quote',
    // Not a landing page anyone should find in search — it is the destination of a CTA.
    noindex: true,
  });
}

export default function Quote() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const form = new FormData(event.currentTarget);
    const get = (k: string) => String(form.get(k) ?? '').trim();

    // Validate on the client for a fast response; the API validates again for real.
    const next: Record<string, string> = {};
    if (!get('name')) next.name = 'Please tell us your name';
    if (!isValidPhone(get('phone'))) next.phone = 'Please enter a 10-digit phone number';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const sqftRaw = get('sqft');
    const payload = {
      name: get('name'),
      phone: get('phone'),
      email: get('email') || undefined,
      company: get('company') || undefined,
      zip: get('zip') || undefined,
      facilityType: (get('facilityType') || undefined) as LeadSubmissionPayload['facilityType'],
      frequency: (get('frequency') || undefined) as LeadSubmissionPayload['frequency'],
      sqft: sqftRaw ? Number(sqftRaw.replace(/[^\d]/g, '')) : undefined,
      message: get('message') || undefined,
      consent: form.get('consent') === 'on',
      sourcePage: '/quote',
      // Honeypot — a real person never fills this; it is off-screen.
      website: get('website') || undefined,
    } satisfies LeadSubmissionPayload & { website?: string };

    setSubmitting(true);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as LeadSubmissionResponse;

      if (data.ok) {
        navigate('/thank-you');
        return;
      }
      setErrors(data.fieldErrors ?? {});
      setFormError(data.error ?? 'Something went wrong. Please email us instead.');
    } catch {
      setFormError(
        `We could not send that. Please email ${SITE.contact.email} and we will take it down directly.`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="section">
      <div className="shell">
        <div className="split">
          <div className="stack">
            <p className="eyebrow-pill">Get a quote</p>
            <h1>Tell us about the building.</h1>
            <p className="lede">
              Seven fields. We call you back the same business day, and if the building is a fit we
              come and walk it.
            </p>

            <hr className="rule" />

            <div className="stack-sm">
              <h4>What happens next</h4>
              <ul className="check">
                <li>We call you back — same business day, usually within the hour.</li>
                <li>Twenty minutes walking the space, counting what is actually there.</li>
                <li>A fixed price in writing, with the crew size and schedule stated.</li>
                <li>If you go ahead: month to month, 30 days out, no termination fee.</li>
              </ul>
            </div>

            <div className="callout">
              <p>
                <strong>Would rather just write?</strong>{' '}
                <a href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>. Same business
                day, every time.
              </p>
            </div>
          </div>

          <div>
            <form className="form" onSubmit={onSubmit} noValidate>
              {formError && (
                <p className="form-status is-error" role="alert">
                  {formError}
                </p>
              )}

              <div className={`field${errors.name ? ' has-error' : ''}`}>
                <label htmlFor="name">Your name</label>
                <input id="name" name="name" type="text" autoComplete="name" required />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              <div className={`field${errors.phone ? ' has-error' : ''}`}>
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="company">Company</label>
                  <input id="company" name="company" type="text" autoComplete="organization" />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" autoComplete="email" />
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="facilityType">Kind of space</label>
                  <select id="facilityType" name="facilityType" defaultValue="office">
                    {FACILITY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {FACILITY_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="sqft">Approximate size</label>
                  <input
                    id="sqft"
                    name="sqft"
                    type="text"
                    inputMode="numeric"
                    placeholder="5,000 sq ft"
                  />
                  <span className="hint">A guess is fine.</span>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="frequency">How often</label>
                  <select id="frequency" name="frequency" defaultValue="3x">
                    {FREQUENCY_TIERS.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="zip">ZIP code</label>
                  <input id="zip" name="zip" type="text" inputMode="numeric" autoComplete="postal-code" />
                </div>
              </div>

              <div className="field">
                <label htmlFor="message">Anything we should know?</label>
                <textarea id="message" name="message" rows={3} />
              </div>

              {/* Honeypot. Off-screen, never announced, never tabbable. */}
              <div className="honeypot" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="consent">
                <input id="consent" name="consent" type="checkbox" />
                <label htmlFor="consent">
                  It is fine to text or call me at this number about my request. Message rates may
                  apply. We do not sell or share your details, ever.
                </label>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send it'}
              </button>

              <p className="small">
                Or email <a href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
